/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
* A foreign key shown as a link to the related record, with icons to choose, change or clear it.
* The candidate list is never loaded until the user asks for it, so any filter that depends on
* other fields is resolved against live form state at that moment rather than being maintained.
*/
import { HttpParams } from '@angular/common/http';
import { UntypedFormGroup } from '@angular/forms';
import { Params } from '@angular/router';
import { AbstractObject, TenantId } from '@src/app/model/abstract-object';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { Field, FieldType, FilterOptions } from '@src/app/shared/field/Field';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { NavRoute } from '@src/app/shared/NavRoute';

/** What a routerLink accepts - a url on its own, or a command array carrying matrix params */
export type RouterTarget = string | unknown[];

export interface PickOptions {
    /** Service the candidate rows are listed from */
    service: AbstractHttpService;
    /** Columns for the pick dialog - by convention <Model>.pickDialogFormConfig() */
    config: () => FormConfig;
    /** Query params for the candidate list, resolved when the picker opens */
    filter?: (item: AbstractObject) => Record<string, string | number>;
    /** Read the candidates from another tenant. Omitted means no _forceTeam is sent */
    forceTeamId?: (item: AbstractObject) => TenantId;
    /** Text to display for the row the user picked. Defaults to the row's name */
    textFrom?: (picked: AbstractObject) => string;
    /** Offer the bin icon, to blank the value */
    allowNull?: boolean;
}

export interface PickLinkOptions {
    label: string;
    /** The foreign key this field holds and posts, e.g. 'unitId' */
    value: string;
    /**
     * The display text, read from the parent record - either the name of a property holding it
     * ('unitName'), or a function that builds it. Whatever this produces should match what
     * pick.textFrom produces for the same record, or the label changes shape after a pick.
     */
    textValue: string | ((item: AbstractObject) => string);
    /** Where the link navigates to */
    nav: NavRoute;
    /**
     * Navigate to the nav's list view filtered by these params, rather than to the record itself.
     * For a foreign key with no page of its own - the owner of a request has no user page, but a
     * list of that owner's open requests says more than the record would have anyway.
     */
    navFilter?: (item: AbstractObject) => Params;
    /** Omit for a display only link - there is nothing to choose from, so no icons are offered */
    pick?: PickOptions;
    /** Run after the value changes, so dependent fields can react */
    refreshes?: ((picked: AbstractObject) => void)[];
}

export class PickLinkField extends Field {
    /*
     * TODO enable filtering. The table filter component only offers a filter UI for a known set of
     * types, so a pickLink column cannot be filtered on today. The filter should open the same
     * picker, and then query on the id of whatever was chosen - not on the display text.
     */
    type: FieldType = 'pickLink';

    textValue: string | ((item: AbstractObject) => string);
    nav: NavRoute;
    navFilter: (item: AbstractObject) => Params = null;
    pick: PickOptions = null;
    refreshes: ((picked: AbstractObject) => void)[] = [];

    /**
     * The column filter's options - NOT the picker's. Self populated from the rows already on the
     * table, so filtering offers the values actually present rather than every possible one.
     */
    filterOptions: FilterOptions = {
        items: [],
        selfPopulate: true,
        makeOption: (item: AbstractObject) => ({ id: this.getValue(item), name: this.getFormattedValue(item) }),
        optionValue: Field.optionValueById,
        optionDisplayValue: Field.optionDisplayByName,
    };

    /** No editable list of its own - the picker fetches on click and keeps nothing */
    getFilterOptions(): FilterOptions {
        return this.filterOptions;
    }

    /** What the link renders - from textValue on load, from the picked row after a pick */
    displayText = '';

    /** Tenant of the record being linked to. Only a new parent record can change it, not a pick */
    private navTeamId: TenantId = null;

    /** The parent as last loaded, so filters can read properties that are not on the form */
    private lastItem: AbstractObject = null;

    setValue(item: AbstractObject, readonly = false) {
        super.setValue(item, readonly);
        this.lastItem = item;
        this.displayText = this.getFormattedValue(item);
        this.navTeamId = this.pick?.forceTeamId ? this.pick.forceTeamId(item) : null;
    }

    /** Null while there is no value, which renders the text as plain text rather than a link */
    getUrl(): RouterTarget {
        const id = this.control?.value;
        if (!id) {
            return null;
        }
        if (this.navFilter) {
            return this.nav.getListRouterLink(this.navFilter(this.getCurrentItem()));
        }
        return this.nav.getIdUrl(id, this.navTeamId);
    }

    /**
     * Tables share one field across every row and render from the item rather than the control,
     * so anything row specific has to be derived here and not read from displayText.
     */
    getUrlFor(item: AbstractObject): RouterTarget {
        const id = this.getValue(item);
        if (!id) {
            return null;
        }
        if (this.navFilter) {
            return this.nav.getListRouterLink(this.navFilter(item));
        }
        return this.nav.getIdUrl(id, this.pick?.forceTeamId ? this.pick.forceTeamId(item) : null);
    }

    /** The name rather than the raw foreign key - the link text, table cell and filter option label */
    getFormattedValue(item: AbstractObject): string {
        if (!this.textValue) {
            return '';
        }
        if (typeof this.textValue === 'function') {
            return this.textValue(item);
        }
        return this.resolveValue(this.textValue, item);
    }

    /** Apply the row the user picked. Pass null to blank the field */
    setPicked(row: AbstractObject) {
        this.control.setValue(row ? row.id : null);
        this.control.markAsDirty();
        this.displayText = row ? this.getPickedText(row) : '';
        this.refreshes.forEach(fn => fn(row));
    }

    clear() {
        this.setPicked(null);
    }

    /**
     * The parent overlaid with current form values, so a filter reading a sibling field sees what
     * is on screen now rather than what was loaded. getRawValue() rather than value, because
     * Angular drops disabled controls from value and those still matter to a filter.
     */
    getCurrentItem(): AbstractObject {
        const parent = this.control?.parent as UntypedFormGroup;
        if (!parent?.getRawValue) {
            return this.lastItem;
        }
        return { ...this.lastItem, ...parent.getRawValue() } as AbstractObject;
    }

    getListParams(item: AbstractObject): HttpParams {
        let params = new HttpParams();
        if (!this.pick?.filter) {
            return params;
        }
        const filter = this.pick.filter(item);
        Object.keys(filter).forEach(key => {
            if (filter[key] !== null && filter[key] !== undefined) {
                params = params.set(key, filter[key]);
            }
        });
        return params;
    }

    getForceTeamId(item: AbstractObject): TenantId {
        return this.pick?.forceTeamId ? this.pick.forceTeamId(item) : null;
    }

    private getPickedText(row: AbstractObject): string {
        if (this.pick?.textFrom) {
            return this.pick.textFrom(row);
        }
        return row.name ?? '';
    }
}
